import { collection, doc, serverTimestamp, Transaction } from 'firebase/firestore';
import {
    Notification,
    NotificationBundleEntry,
    NotificationBundleEntryStatus,
    NotificationBundlesMeta,
} from '#types/System.types';
import { COMMUNICATION_LOG_BUNDLE_SIZE } from '#types/System.Parameters.types';
import { firestore } from './startFirestore';

type NotificationBundleDoc = {
    docType: 'notificationBundle';
    entries: NotificationBundleEntry[];
    entryCount: number;
    createdAt: unknown;
    updatedAt: unknown;
};

type NotificationBundlesMetaContainer = {
    notificationBundlesMeta?: NotificationBundlesMeta;
};

type AppendEntryParams = {
    transaction: Transaction;
    userId: string;
    userDocData: NotificationBundlesMetaContainer;
    entry: NotificationBundleEntry;
};

type ReplaceEntryParams = {
    transaction: Transaction;
    userId: string;
    userDocData: NotificationBundlesMetaContainer;
    bundleDocId: string;
    entry: NotificationBundleEntry;
};

type RemoveEntryParams = {
    transaction: Transaction;
    userId: string;
    userDocData: NotificationBundlesMetaContainer;
    bundleDocId: string;
    notificationEventId: string;
};

type BulkStatusParams = {
    transaction: Transaction;
    userId: string;
    userDocData: NotificationBundlesMetaContainer;
    entries: NotificationBundleEntry[];
    bundleDocIdByEventId: Map<string, string>;
    status: NotificationBundleEntryStatus;
};

type BulkRemoveParams = {
    transaction: Transaction;
    userId: string;
    userDocData: NotificationBundlesMetaContainer;
    entries: NotificationBundleEntry[];
    bundleDocIdByEventId: Map<string, string>;
};

export const NotificationBundles = {
    appendEntry: async ({
        transaction,
        userId,
        userDocData,
        entry,
    }: AppendEntryParams): Promise<void> => {
        const notificationEventId = entry.notification.notificationEvent?.docId;
        if (!notificationEventId) {
            throw new Error('Invalid notification data');
        }

        const bundlesPath = `users/${userId}/notificationBundles`;
        const userDocRef = doc(firestore, 'users', userId);
        const meta = userDocData.notificationBundlesMeta;
        const currentBundleDocId = meta?.currentBundleDocId;
        const currentBundleEntryCount = meta?.currentBundleEntryCount ?? 0;

        if (currentBundleDocId && currentBundleEntryCount < COMMUNICATION_LOG_BUNDLE_SIZE) {
            const currentBundleRef = doc(firestore, bundlesPath, currentBundleDocId);
            const currentBundleSnap = await transaction.get(currentBundleRef);

            if (currentBundleSnap.exists()) {
                const currentEntries = ((currentBundleSnap.data() as Partial<NotificationBundleDoc>).entries ?? []);
                const entryExists = currentEntries.some((item) => (
                    item?.notification?.notificationEvent?.docId === notificationEventId
                ));
                const nextEntries = entryExists
                    ? currentEntries.map((item) => (
                        item?.notification?.notificationEvent?.docId === notificationEventId
                            ? entry
                            : item
                    ))
                    : [entry, ...currentEntries];

                transaction.update(currentBundleRef, {
                    entries: nextEntries,
                    entryCount: nextEntries.length,
                    updatedAt: serverTimestamp(),
                });

                transaction.update(userDocRef, {
                    'notificationBundlesMeta.currentBundleEntryCount': nextEntries.length,
                });
                return;
            }
        }

        const bundlesCollectionRef = collection(firestore, bundlesPath);
        const newBundleRef = doc(bundlesCollectionRef);
        const bundleDoc: NotificationBundleDoc = {
            docType: 'notificationBundle',
            entries: [entry],
            entryCount: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        transaction.set(newBundleRef, bundleDoc);

        const nextMeta: NotificationBundlesMeta = {
            currentBundleDocId: newBundleRef.id,
            currentBundleEntryCount: 1,
        };

        transaction.update(userDocRef, {
            notificationBundlesMeta: nextMeta,
        });
    },

    replaceEntry: async ({
        transaction,
        userId,
        userDocData,
        bundleDocId,
        entry,
    }: ReplaceEntryParams): Promise<void> => {
        const notificationEventId = entry.notification.notificationEvent?.docId;
        if (!notificationEventId) {
            throw new Error('Invalid notification data');
        }

        const bundlesPath = `users/${userId}/notificationBundles`;
        const userDocRef = doc(firestore, 'users', userId);
        const bundleRef = doc(firestore, bundlesPath, bundleDocId);
        const bundleSnap = await transaction.get(bundleRef);

        if (!bundleSnap.exists()) {
            throw new Error(`Notification bundle not found: ${bundleDocId}`);
        }

        const currentEntries = ((bundleSnap.data() as Partial<NotificationBundleDoc>).entries ?? []);
        const entryExists = currentEntries.some((item) => (
            item?.notification?.notificationEvent?.docId === notificationEventId
        ));
        const nextEntries = entryExists
            ? currentEntries.map((item) => (
                item?.notification?.notificationEvent?.docId === notificationEventId
                    ? entry
                    : item
            ))
            : [entry, ...currentEntries];

        transaction.update(bundleRef, {
            entries: nextEntries,
            entryCount: nextEntries.length,
            updatedAt: serverTimestamp(),
        });

        const currentMeta = userDocData.notificationBundlesMeta;
        if (currentMeta?.currentBundleDocId === bundleDocId) {
            transaction.update(userDocRef, {
                'notificationBundlesMeta.currentBundleEntryCount': nextEntries.length,
            });
        }
    },

    setEntryStatus: async ({
        transaction,
        userId,
        userDocData,
        bundleDocId,
        notification,
        status,
    }: {
        transaction: Transaction;
        userId: string;
        userDocData: NotificationBundlesMetaContainer;
        bundleDocId: string;
        notification: Notification;
        status: NotificationBundleEntryStatus;
    }): Promise<void> => {
        return NotificationBundles.replaceEntry({
            transaction,
            userId,
            userDocData,
            bundleDocId,
            entry: { notification, status },
        });
    },

    removeEntry: async ({
        transaction,
        userId,
        userDocData,
        bundleDocId,
        notificationEventId,
    }: RemoveEntryParams): Promise<void> => {
        const bundlesPath = `users/${userId}/notificationBundles`;
        const userDocRef = doc(firestore, 'users', userId);
        const bundleRef = doc(firestore, bundlesPath, bundleDocId);
        const bundleSnap = await transaction.get(bundleRef);

        if (!bundleSnap.exists()) {
            throw new Error(`Notification bundle not found: ${bundleDocId}`);
        }

        const currentEntries = ((bundleSnap.data() as Partial<NotificationBundleDoc>).entries ?? []);
        const nextEntries = currentEntries.filter((item) => (
            item?.notification?.notificationEvent?.docId !== notificationEventId
        ));

        if (nextEntries.length === currentEntries.length) {
            return;
        }

        transaction.update(bundleRef, {
            entries: nextEntries,
            entryCount: nextEntries.length,
            updatedAt: serverTimestamp(),
        });

        const currentMeta = userDocData.notificationBundlesMeta;
        if (currentMeta?.currentBundleDocId === bundleDocId) {
            transaction.update(userDocRef, {
                'notificationBundlesMeta.currentBundleEntryCount': nextEntries.length,
            });
        }
    },

    setAllEntriesStatus: async ({
        transaction,
        userId,
        userDocData,
        entries,
        bundleDocIdByEventId,
        status,
    }: BulkStatusParams): Promise<void> => {
        const bundlesPath = `users/${userId}/notificationBundles`;
        const userDocRef = doc(firestore, 'users', userId);
        const bundleUpdates = new Map<string, NotificationBundleEntry[]>();

        entries.forEach((entry) => {
            const eventId = entry?.notification?.notificationEvent?.docId;
            if (!eventId) return;
            const bundleDocId = bundleDocIdByEventId.get(eventId);
            if (!bundleDocId) return;

            const existing = bundleUpdates.get(bundleDocId) ?? [];
            existing.push({ ...entry, status });
            bundleUpdates.set(bundleDocId, existing);
        });

        for (const [bundleDocId, updatedEntries] of bundleUpdates) {
            const bundleRef = doc(firestore, bundlesPath, bundleDocId);
            const bundleSnap = await transaction.get(bundleRef);
            if (!bundleSnap.exists()) continue;

            const currentEntries = ((bundleSnap.data() as { entries?: NotificationBundleEntry[] }).entries ?? []);
            const updatedEventIds = new Set(updatedEntries.map(e => e.notification?.notificationEvent?.docId));

            const nextEntries = currentEntries.map((item) => {
                const itemEventId = item?.notification?.notificationEvent?.docId;
                if (itemEventId && updatedEventIds.has(itemEventId)) {
                    return { ...item, status };
                }
                return item;
            });

            transaction.update(bundleRef, {
                entries: nextEntries,
                updatedAt: serverTimestamp(),
            });

            const currentMeta = userDocData.notificationBundlesMeta;
            if (currentMeta?.currentBundleDocId === bundleDocId) {
                transaction.update(userDocRef, {
                    'notificationBundlesMeta.currentBundleEntryCount': nextEntries.length,
                });
            }
        }
    },

    removeAllEntries: async ({
        transaction,
        userId,
        userDocData,
        entries,
        bundleDocIdByEventId,
    }: BulkRemoveParams): Promise<void> => {
        const bundlesPath = `users/${userId}/notificationBundles`;
        const userDocRef = doc(firestore, 'users', userId);
        const bundleRemovals = new Map<string, Set<string>>();

        entries.forEach((entry) => {
            const eventId = entry?.notification?.notificationEvent?.docId;
            if (!eventId) return;
            const bundleDocId = bundleDocIdByEventId.get(eventId);
            if (!bundleDocId) return;

            const existing = bundleRemovals.get(bundleDocId) ?? new Set();
            existing.add(eventId);
            bundleRemovals.set(bundleDocId, existing);
        });

        for (const [bundleDocId, eventIdsToRemove] of bundleRemovals) {
            const bundleRef = doc(firestore, bundlesPath, bundleDocId);
            const bundleSnap = await transaction.get(bundleRef);
            if (!bundleSnap.exists()) continue;

            const currentEntries = ((bundleSnap.data() as { entries?: NotificationBundleEntry[] }).entries ?? []);
            const nextEntries = currentEntries.filter((item) => {
                const itemEventId = item?.notification?.notificationEvent?.docId;
                return !itemEventId || !eventIdsToRemove.has(itemEventId);
            });

            transaction.update(bundleRef, {
                entries: nextEntries,
                entryCount: nextEntries.length,
                updatedAt: serverTimestamp(),
            });

            const currentMeta = userDocData.notificationBundlesMeta;
            if (currentMeta?.currentBundleDocId === bundleDocId) {
                transaction.update(userDocRef, {
                    'notificationBundlesMeta.currentBundleEntryCount': nextEntries.length,
                });
            }
        }
    },
};
